import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Flex,
  Avatar,
  Box,
  Text,
  Divider,
  Button,
  HStack,
  Input,
  FormLabel,
  FormControl,
  InputGroup,
  Switch,
  Spacer,
  Tooltip,
  AvatarBadge,
  Icon,
  InputRightElement,
  useToast
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { createUsersApi } from '../../../../utils/api-interface'
import { DashboardProfile, UserDetailsResponse, UserStatus, UserUpdateRequest } from '../../../../services/network'
import { ParsedCountry, PhoneInput } from 'react-international-phone'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa'
import { SingleDatepicker } from '../../../../single'
import { convertDateToItalian, getErrorMessage } from '../../../../utils/utils'
import { DeleteConfirmationDialog } from '../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog'
import { log } from '../../../../utils/logger'
import './phone-style.css'
import dayjs from 'dayjs'
import { json2csv } from 'json-2-csv'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../store/store'

export interface IUserInfoProps {
  onUserUpdate: () => void
  isUserDeleted: boolean
  user: UserDetailsResponse | undefined
}

function UserInfo({ onUserUpdate, isUserDeleted, user }: IUserInfoProps): JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams()
  const toast = useToast()

  const [userUpdateRequest, setUserUpdateRequest] = useState<UserUpdateRequest>()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [banUser, setBanUser] = useState(false)
  const [suspendUser, setSuspendUser] = useState(false)

  const [loadingBanSuspend, isLoadingBanSuspend] = useState<boolean>(false)
  const [loadingDelete, isLoadingDelete] = useState<boolean>(false)
  const [loadingResetPassword, isLoadingResetPassword] = useState<boolean>(false)
  const [loadingUpdate, isLoadingUpdate] = useState<boolean>(false)

  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const canUpdate = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR || dashboardUser.role.title === process.env.REACT_APP_ROLE_BACKOFFICE
  }, [dashboardUser])

  const controlEnabled = useMemo(() => {
    if (loadingBanSuspend || loadingDelete || loadingResetPassword || loadingUpdate) {
      return false
    }
    return true
  }, [loadingBanSuspend, loadingDelete, loadingResetPassword, loadingUpdate])

  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true)

  const handleFirstnameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          firstName: e.target.value
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleLastnameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          lastName: e.target.value
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          email: e.target.value
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleUserNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          username: e.target.value
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handlePhoneNumberChange = useCallback(
    (
      phone: string,
      meta: {
        country: ParsedCountry
        inputValue: string
      }
    ): void => {
      if (canUpdate) {
        log.debug(`[handlePhoneNumberChange] meta: ${meta}`)
        setUserUpdateRequest({
          ...userUpdateRequest,
          phoneNumber: phone
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleMarketingChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          isMarketingEnabled: event.target.checked
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const setDate = useCallback(
    (date: Date) => {
      if (canUpdate) {
        setUserUpdateRequest({
          ...userUpdateRequest,
          birthday: dayjs(date).toISOString()
        })
      }
    },
    [canUpdate, userUpdateRequest]
  )

  const handleProfilePic = useCallback(async (): Promise<void> => {
    if (canUpdate) {
      if (user) {
        try {
          await createUsersApi().deleteUserProfileImage(user.id)
          onUserUpdate()
        } catch (e: any) {
          toast({
            title: t('profile_pic_delete_error'),
            description: e.message,
            status: 'error',
            duration: 4000,
            isClosable: true
          })
        }
      }
    }
  }, [canUpdate, onUserUpdate, t, toast, user])

  const handleCloseDialog = (): void => {
    setIsDialogOpen(false)
  }

  const banUserApi = useCallback(
    async (banned: boolean) => {
      if (loadingBanSuspend || !user || !canUpdate) {
        return
      }

      isLoadingBanSuspend(true)

      try {
        const response = await createUsersApi().setUserBanStatus(user.id, { banned })
        if (response.status === 200) {
          toast({ title: banned ? t('ban_user_success') : t('unban_user_success'), status: 'success', duration: 4000, isClosable: true })
        }

        onUserUpdate()
      } catch (e: any) {
        toast({ title: banned ? t('error_ban_user') : t('error_unban_user'), description: e.message, status: 'error', duration: 4000, isClosable: true })
      }

      isLoadingBanSuspend(false)
    },
    [canUpdate, loadingBanSuspend, onUserUpdate, t, toast, user]
  )

  const suspendUserApi = useCallback(
    async (deactivated: boolean) => {
      if (loadingBanSuspend || !user || !canUpdate) {
        return
      }

      isLoadingBanSuspend(true)

      try {
        const response = await createUsersApi().setUserDeactivationStatus(user.id, { deactivated })
        if (response.status === 200) {
          onUserUpdate()
          toast({ title: deactivated ? t('suspend_user_success') : t('reactivate_user_success'), status: 'success', duration: 4000, isClosable: true })
        } else {
          setSuspendUser(!deactivated)
        }
      } catch (e: any) {
        toast({
          title: deactivated ? t('error_suspend_user') : t('error_reactivate_user'),
          description: e.message,
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      }

      isLoadingBanSuspend(false)
    },
    [canUpdate, loadingBanSuspend, onUserUpdate, t, toast, user]
  )

  const handleBan = useCallback(
    (e: { target: { checked: boolean } }): void => {
      if (canUpdate) {
        setBanUser(e.target.checked)
        banUserApi(e.target.checked)
      }
    },
    [banUserApi, canUpdate]
  )

  const handleSuspend = useCallback(
    (e: { target: { checked: boolean } }): void => {
      if (canUpdate) {
        setSuspendUser(e.target.checked)
        suspendUserApi(e.target.checked)
      }
    },
    [canUpdate, suspendUserApi]
  )

  const updateProfile = useCallback(async (): Promise<void> => {
    if (!id || !userUpdateRequest || !controlEnabled || !canUpdate) {
      return
    }

    isLoadingUpdate(true)
    try {
      const usersApi = createUsersApi()
      log.debug(userUpdateRequest)
      const response = await usersApi.updateUser(id, userUpdateRequest)
      log.debug(response.data)
      onUserUpdate()
      toast({ title: t('update_user_success'), status: 'success', duration: 4000, isClosable: true })
    } catch (error: any) {
      log.error('Unexpected error:', error)
      toast({ title: t('error_update_user'), description: getErrorMessage(error), status: 'error', duration: 4000, isClosable: true })
    }
    isLoadingUpdate(false)
  }, [canUpdate, controlEnabled, id, onUserUpdate, t, toast, userUpdateRequest])

  const handleUpdate = (): void => {
    updateProfile()
  }

  const deleteProfile = useCallback(async (): Promise<void> => {
    if (!user || !controlEnabled || !canUpdate) {
      return
    }
    isLoadingDelete(true)
    if (id) {
      const usersApi = createUsersApi()
      usersApi
        .deleteUser(id)
        .then((data) => {
          log.debug(`[deleteProfile] data: ${data}`)
          toast({ title: t('delete_user_success'), status: 'success', duration: 4000, isClosable: true })
        })
        .catch((error: any) => {
          log.error('Error fetching search results:', error)
          toast({ title: t('error_delete_user'), description: error.message, status: 'error', duration: 4000, isClosable: true })
        })
        .finally(() => isLoadingDelete(false))
    }
    setIsDialogOpen(false)
  }, [canUpdate, controlEnabled, id, t, toast, user])

  useEffect((): void => {
    eva.replace()
  })

  useEffect((): void => {
    if (user && !isUserDeleted) {
      setUserUpdateRequest({
        firstName: user.firstName,
        username: user.username,
        lastName: user.lastName,
        birthday: user.birthday,
        bio: user.profile?.bio,
        phoneNumber: user.phoneNumber,
        documents: user.documents,
        address: user.address,
        email: user.email,
        isMarketingEnabled: user.isMarketingEnabled ?? false
      })
      setSaveButtonDisabled(false)
      if (user.status === UserStatus.Banned) {
        setBanUser(true)
        setSuspendUser(false)
      } else if (user.status === UserStatus.Suspended) {
        setBanUser(false)
        setSuspendUser(true)
      } else {
        setBanUser(false)
        setSuspendUser(false)
      }
    }
  }, [user, isUserDeleted])

  const stars = []
  for (let i = 1; i <= 5; i++) {
    if (user?.stats?.averageRating) {
      if (user.stats.averageRating >= i) {
        stars.push(<Icon as={FaStar} key={i} color='yellow.500' />)
      } else if (user.stats.averageRating >= i - 0.5) {
        stars.push(<Icon as={FaStarHalfAlt} key={i} color='yellow.500' />)
      } else {
        stars.push(<Icon as={FaRegStar} key={i} color='yellow.500' />)
      }
    }
  }

  const handleResetPassword = useCallback(async () => {
    if (!id || !controlEnabled || !canUpdate) {
      if (!canUpdate) {
        toast({
          title: t('reset_password.error'),
          description: t('reset_password.error_permissions'),
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      }
      return
    }

    isLoadingResetPassword(true)

    try {
      await createUsersApi().resetUserPassword(id)
      toast({
        title: t('reset_password.link_success_title'),
        description: t('reset_password.link_success_text'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    } catch (error: any) {
      log.error('Error fetching search results:', error)
      toast({ title: t('reset_password.error'), description: error.message, status: 'error', duration: 4000, isClosable: true })
    }

    isLoadingResetPassword(false)
  }, [canUpdate, controlEnabled, id, t, toast])

  const generateCSV = useCallback(async () => {
    if (!id || !canUpdate) {
      return
    }
    try {
      const jsonData = await createUsersApi().getUser(id)
      // Converti i dati JSON in CSV
      const csvContent = await json2csv([jsonData.data])
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)

      // Crea e attiva il download
      const a = document.createElement('a')
      a.href = url
      a.download = `${dayjs().format('YYYYMMDD')}_user_${id}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast({
        title: t('csv_success'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    } catch (error) {
      log.error('Errore nella conversione JSON a CSV', error)
      toast({
        title: t('csv_error'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  }, [canUpdate, id, t, toast])

  const provider = useMemo(() => {
    if (!user || !user.provider) {
      return (
        <FormControl id='login-provider' width={'50%'}>
          <FormLabel variant={'primary'}>{t('login_provider')}</FormLabel>
          <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'greyCold300'} textTransform={'capitalize'}>
            {t('missing_provider')}
          </Text>
        </FormControl>
      )
    }
    if (user.provider === 'email') {
      return (
        <FormControl id='login-provider' width={'50%'}>
          <FormLabel variant={'primary'}>{t('login_provider')}</FormLabel>
          <Button variant={'secondary'} width={'50%'} onClick={handleResetPassword} disabled={isUserDeleted}>
            {t('reset_password.button_title')}
          </Button>
        </FormControl>
      )
    }

    return (
      <FormControl id='login-provider' width={'50%'}>
        <FormLabel variant={'primary'}>{t('login_provider')}</FormLabel>
        <Input bg='white' defaultValue={user.provider} color={'black'} disabled={true} />
      </FormControl>
    )
  }, [user, t, handleResetPassword, isUserDeleted])

  return (
    <Flex direction={'column'}>
      <HStack>
        <Flex my={5}>
          <Avatar size='xl' name='avatar' src={user?.profile?.profileImageUrl}>
            {canUpdate && (
              <AvatarBadge boxSize='1.25em' bg='white'>
                <Button onClick={handleProfilePic} minWidth='fit-content'>
                  <i data-eva={'trash-2'} data-eva-fill={'#353F48'}></i>
                </Button>
              </AvatarBadge>
            )}
          </Avatar>
          <Flex direction={'column'} ml={5} width={'351px'}>
            <Flex height={'42px'}>
              <Flex direction='column' alignItems={'center'} gap={'6px'} padding={'5px'} marginRight={'25px'}>
                <Text fontSize='md' fontWeight='bold'>
                  {user?.stats?.createdEvents}
                </Text>
                <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'greyCold300'}>
                  {t('events_created')}
                </Text>
              </Flex>
              <Divider border='1px' orientation='vertical' gap={'10px'} />
              <Flex direction='column' alignItems={'center'} gap={'6px'} padding={'5px'} marginRight={'25px'} marginLeft={'25px'}>
                <Text fontSize='md' fontWeight='bold'>
                  {user?.stats?.reservedEvents}
                </Text>
                <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'greyCold300'}>
                  {t('events_reserved')}
                </Text>
              </Flex>
              <Divider border='1px' orientation='vertical' />
              <Flex direction='column' alignItems={'center'} gap={'6px'} padding={'5px'} marginRight={'25px'} marginLeft={'25px'}>
                <Text fontSize='md' fontWeight='bold'>
                  {user?.stats?.feedbacksSent}
                </Text>
                <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'greyCold300'}>
                  {t('reviews_sent')}
                </Text>
              </Flex>
              <Divider border='1px' orientation='vertical' />
              <Flex direction='column' alignItems={'center'} gap={'6px'} padding={'5px'} marginLeft={'25px'}>
                <Text fontSize='md' fontWeight='bold'>
                  {user?.stats?.friends}
                </Text>
                <Text fontSize='12px' alignItems={'center'} fontWeight='500' lineHeight='14px' color={'greyCold300'}>
                  {t('friends')}
                </Text>
              </Flex>
            </Flex>
            <Text mt='24px' fontSize='12px' fontWeight='500' lineHeight='14px' color={'greyWarm300'}>
              {user?.profile?.bio}
            </Text>
          </Flex>
        </Flex>
        <Spacer></Spacer>
        <Flex direction={'column'}>
          {canUpdate ? (
            <Button variant='outline' key={'export'} onClick={generateCSV}>
              <i data-eva={'download-outline'} data-eva-fill={'#353F48'}></i>
              {t('export_csv')}
            </Button>
          ) : (
            <div style={{ height: '40px', width: '185px' }}></div>
          )}
          <HStack marginTop={'24px'}>
            {stars}
            <Text fontSize='12px' fontWeight='700' lineHeight='16.8px' color={'#9CA3AF'} letterSpacing={'3%'}>
              ({user?.stats?.feedbacksReceived})
            </Text>
          </HStack>
          <Text fontSize='12px' fontWeight='500' lineHeight='14px' color={'greyWarm300'}>
            {t('inscribed_since')} {dayjs(user?.registrationDate).locale('it').format(`DD/MM/YYYY`)}
          </Text>
        </Flex>
      </HStack>
      <Flex direction={'column'} rowGap={'24px'}>
        <HStack>
          <FormControl id='firstname'>
            <FormLabel variant={'primary'}>{t('first_name')}</FormLabel>
            <Input bg='white' value={userUpdateRequest?.firstName} color={'black'} onInput={handleFirstnameChange} disabled={!canUpdate || isUserDeleted} />
          </FormControl>
          <FormControl id='birthdate'>
            <FormLabel variant={'primary'}>{t('birth_date')}</FormLabel>
            <InputGroup isolation={'auto'}>
              <Input
                bg={'white'}
                variant={!canUpdate || isUserDeleted ? undefined : 'date'}
                value={convertDateToItalian(userUpdateRequest?.birthday ?? '1970-01-01')}
                color={'black'}
                isDisabled={!canUpdate || isUserDeleted}
              />
              {canUpdate && (
                <InputRightElement width={'50px'}>
                  <SingleDatepicker
                    name='date-input'
                    date={dayjs(userUpdateRequest?.birthday).toDate()}
                    onDateChange={setDate}
                    disabled={!canUpdate || isUserDeleted}
                  />
                </InputRightElement>
              )}
            </InputGroup>
          </FormControl>
        </HStack>
        <HStack>
          <FormControl id='lastname'>
            <FormLabel variant={'primary'}>{t('last_name')}</FormLabel>
            <Input bg='white' value={userUpdateRequest?.lastName} color={'black'} onChange={handleLastnameChange} disabled={!canUpdate || isUserDeleted} />
          </FormControl>
          <FormControl id='email'>
            <FormLabel variant={'primary'}>{t('email')}</FormLabel>
            <Input
              bg='white'
              type='primary'
              value={userUpdateRequest?.email}
              color={'black'}
              onChange={handleEmailChange}
              disabled={user?.provider !== 'email'}
            />
          </FormControl>
        </HStack>
        <HStack>
          <FormControl id='username' width={'50%'}>
            <FormLabel variant={'primary'}>{t('username')}</FormLabel>
            <Input bg='white' value={userUpdateRequest?.username} color={'black'} onChange={handleUserNameChange} disabled={!canUpdate || isUserDeleted} />
          </FormControl>
          {provider}
        </HStack>
        <HStack>
          <FormControl id='phonenumber' width={'24%'}>
            <FormLabel variant={'primary'}>{t('phone_number')}</FormLabel>
            <PhoneInput defaultCountry='it' value={userUpdateRequest?.phoneNumber} onChange={handlePhoneNumberChange} disabled={!canUpdate || isUserDeleted} />
          </FormControl>
          <FormControl id='marketing' width={'25%'}>
            <FormLabel variant={'primary'}>{t('marketing')}</FormLabel>
            <Switch
              variant={'primary'}
              isChecked={userUpdateRequest?.isMarketingEnabled}
              onChange={handleMarketingChange}
              disabled={!canUpdate || isUserDeleted}></Switch>
          </FormControl>
          <Box flex='1'></Box>
        </HStack>
      </Flex>
      {!isUserDeleted && (
        <Flex justify='space-between' align='left' direction={'column'} my={5}>
          <HStack style={{ height: '40px' }}>
            {loadingBanSuspend ? (
              <></>
            ) : (
              <>
                <Switch variant={'primary'} isChecked={banUser} onChange={handleBan} disabled={!controlEnabled}></Switch>
                <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
                  {t('ban_user')}
                </Text>
                <Tooltip hasArrow={true} bg={'#101426'} label={t('tooltip_ban')} fontSize='md' key={'tipban'}>
                  <span>
                    <i data-eva={'question-mark-circle-outline'} data-eva-fill={'#353F48'}></i>
                  </span>
                </Tooltip>
              </>
            )}
            <Spacer></Spacer>
            {loadingDelete && (
              <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
                {t('operation_in_progress', { operation: t('delete_user_op') })}
              </Text>
            )}
            {canUpdate && (
              <Button
                key='deleteaccount'
                disabled={!controlEnabled}
                onClick={(): void => {
                  setIsDialogOpen(true)
                }}>
                <i data-eva={'trash-2'} data-eva-fill={'#353F48'}></i>
                <Text
                  marginLeft={'5px'}
                  fontWeight='600'
                  fontSize='16px'
                  lineHeight='22.4px'
                  color={'greyCold200'}
                  _hover={{
                    color: 'black'
                  }}>
                  {t('delete_permanently')}
                </Text>
              </Button>
            )}
          </HStack>
          <HStack>
            {loadingBanSuspend ? (
              <>
                <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
                  {t('operation_in_progress', { operation: t('ban_or_suspend_user') })}
                </Text>
              </>
            ) : (
              <>
                <Switch variant={'primary'} isChecked={suspendUser} onChange={handleSuspend} disabled={!controlEnabled}></Switch>
                <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
                  {t('suspend_user')}
                </Text>
                <Tooltip hasArrow={true} bg={'#101426'} label={t('tooltip_suspend')} fontSize='md' key={'tipsuspend'}>
                  <span>
                    <i data-eva={'question-mark-circle-outline'} data-eva-fill={'#353F48'}></i>
                  </span>
                </Tooltip>
              </>
            )}
          </HStack>
        </Flex>
      )}
      {canUpdate && !isUserDeleted && (
        <Flex direction={'row-reverse'}>
          {loadingUpdate ? (
            <>
              <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
                {t('operation_in_progress', { operation: t('update_user_op') })}
              </Text>
            </>
          ) : (
            <>
              <Button variant={'primary'} width={'211px'} onClick={handleUpdate} isDisabled={!controlEnabled && saveButtonDisabled}>
                {t('save')}
              </Button>
            </>
          )}
        </Flex>
      )}
      {canUpdate && <DeleteConfirmationDialog isOpen={isDialogOpen} onClose={handleCloseDialog} onConfirm={deleteProfile} type='user' />}
    </Flex>
  )
}

export { UserInfo }
